import { Link } from "react-router-dom";
import { Coffee, MapPin, Lock, Sparkles } from "lucide-react";
import { KAFFA } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";

const QUALITY_BADGE = {
  Specialty: "bg-accent/15 text-accent border-accent/30",
  Microlot: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  Premium: "bg-secondary text-foreground border-border",
  Commercial: "bg-muted text-muted-foreground border-border",
};

export const LotCard = ({ lot, locked = true }) => {
  const { user } = useAuth();
  const { formatPrice, t, tData } = useLocale();
  const isApprovedBuyer = user && user !== false && (user.role !== "buyer" || user.status === "approved");
  const showPrice = !locked || (isApprovedBuyer && lot.price_per_bag != null);

  const displayPrice = lot.display_price ?? lot.price_per_bag;
  const displayCurrency = lot.display_currency ?? lot.currency ?? "BRL";

  return (
    <Link
      to={isApprovedBuyer ? `/lot/${lot.id}` : `/login?next=/lot/${lot.id}`}
      data-testid={KAFFA.lotCardLink}
      className="group block"
    >
      <article
        data-testid={KAFFA.lotCard}
        className="hover-lift border border-border bg-card overflow-hidden flex flex-col h-full hover:border-accent/60"
      >
        <div className="relative aspect-[5/3] overflow-hidden bg-secondary">
          {lot.images?.[0] ? (
            <img
              src={lot.images[0]}
              alt={lot.name}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-secondary">
              <Coffee className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
          <div className="absolute top-3 left-3 flex gap-1.5">
            <span className={`px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border ${QUALITY_BADGE[lot.quality_grade] || QUALITY_BADGE.Premium}`}>
              {tData("quality", lot.quality_grade)}
            </span>
            {lot.sca_score >= 86 && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border border-accent/40 bg-background/80 backdrop-blur text-accent">
                <Sparkles className="h-2.5 w-2.5" /> SCA {lot.sca_score}
              </span>
            )}
          </div>
          {lot.available_for_export && (
            <div className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider bg-background/80 backdrop-blur border border-border">
              FOB · Export
            </div>
          )}
        </div>

        <div className="p-5 flex-1 flex flex-col">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
            {tData("coffee", lot.coffee_type)} · {tData("process", lot.process)}
          </div>
          <h3 className="font-display text-lg leading-tight tracking-tight text-foreground line-clamp-2 group-hover:text-accent transition-colors">
            {lot.name}
          </h3>

          <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{lot.farm_name} · {lot.city}/{lot.state}</span>
            </div>
            {lot.cooperative_name && (
              <div className="flex items-center gap-1.5">
                <Coffee className="h-3 w-3" />
                <span className="truncate">{lot.cooperative_name}</span>
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-border flex items-end justify-between">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("lots.available")}
              </div>
              <div className="font-mono-data text-base text-foreground font-medium">
                {lot.bags_quantity?.toLocaleString()} <span className="text-xs text-muted-foreground">{t("common.bags")}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                {showPrice ? t("lots.price_label") : t("lots.price_label")}
              </div>
              {showPrice ? (
                <div className="font-mono-data text-lg text-accent font-medium">
                  {formatPrice(displayPrice, displayCurrency)}
                </div>
              ) : (
                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                  <Lock className="h-3 w-3" />
                  <span className="font-mono-data text-xs uppercase tracking-wider">{t("nav.login")}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
};
